import { FC, useState } from "react";

import { TaskDriverStatusEnum } from "@/domain/models/common";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsTranslator } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverTopActionButton } from "./DriverTopActionButton";

const DriverAddPhotoAfterCleanupStep: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { mutate: uploadCleanupPhoto, isPending: isUploadingCleanupPhoto } =
    useUploadServicePhotoMutation();

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const openCamera = () => setIsCameraOpen(true);
  const closeCamera = () => setIsCameraOpen(false);

  const uploadPhoto = ({
    base64PhotoString,
    comment,
  }: CameraPhotoWithCommentType) => {
    uploadCleanupPhoto(
      {
        jobId,
        serviceId,
        base64PhotoString,
        baseName: "postjunkremoval",
      },
      {
        onSuccess: path => {
          updateService({
            jobId,
            serviceId,
            postJunkRemovalPhoto: {
              photoUrl: path,
              comment,
            },
            driverFlowStatus: TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD,
          });

          updateTask({
            jobId,
            serviceId,
            taskId,
            driverFlowStatus: TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD,
          });
        },
      },
    );
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_LOAD_SIZE;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="single-photo"
          onChoosePhoto={photo => {
            setIsCameraOpen(false);
            void uploadPhoto(photo);
          }}
          onCancelClick={closeCamera}
        />
      ) : (
        <>
          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsTranslator
              variant="pageTitle"
              i18nProps={{
                i18nKey: "driver_photo_after_cleanup.title",
              }}
            />
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isUploadingCleanupPhoto}
            nextButtonTitle="take_photo"
            previousButtonTitle="driver_photo_after_cleanup.location_access"
            isPreviousButtonLoading={isPreviousButtonLoading}
            onPreviousButtonClick={goToPreviousStep}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddPhotoAfterCleanupStep };
